import React, { useState, useEffect, useCallback } from 'react';
import { Button, Table, Tag, Card, Space, Typography, App, Drawer, Descriptions, Select } from 'antd';
import { ReloadOutlined, LikeOutlined, DislikeOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import feedbackService from '../../services/feedbackService';
import tenantService from '../../services/tenantService';
import logger from '../../utils/logger';

const { Text } = Typography;
const { Option } = Select;

const Feedback = () => {
    const { message } = App.useApp();

    // State management
    const [feedback, setFeedback] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [feedbackTotal, setFeedbackTotal] = useState(0);
    const [statusFilter, setStatusFilter] = useState('all');  // 'all', 'read', 'unread'

    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);

    // Load feedback list
    const loadFeedback = useCallback(async () => {
        try {
            setLoading(true);
            const response = await feedbackService.getFeedbackList({
                sortBy: 'timestamp',
                sortOrder: 'desc',
                limit: 500,
                statusFilter: statusFilter === 'all' ? undefined : statusFilter
            });
            setFeedback(response.feedback || []);
            setFeedbackTotal(response.total || 0);
        } catch (err) {
            message.error(err.message || 'Failed to load feedback');
            logger.error('Error loading feedback:', err);
        } finally {
            setLoading(false);
        }
    }, [message, statusFilter]);

    // Load tenant list for name resolution
    const loadTenants = useCallback(async () => {
        try {
            const tenantList = await tenantService.getTenantList();
            setTenants(tenantList);
        } catch (err) {
            logger.error('Error loading tenants:', err);
        }
    }, []);

    // Load data on mount and when filter changes
    useEffect(() => {
        loadFeedback();
        loadTenants();
    }, [loadFeedback, loadTenants]);

    // Handle row click - open drawer
    const handleRowClick = (record) => {
        setSelectedFeedback(record);
        setDrawerOpen(true);
    };

    // Handle mark as read
    const handleMarkAsRead = async () => {
        if (!selectedFeedback) return;

        try {
            await feedbackService.markAsRead(selectedFeedback.tenant_id, selectedFeedback.feedback_id);
            message.success('Feedback marked as read');

            // Update local state
            setFeedback(prevFeedback =>
                prevFeedback.map(fb =>
                    fb.feedback_id === selectedFeedback.feedback_id
                        ? { ...fb, is_read: true }
                        : fb
                )
            );
            setSelectedFeedback({ ...selectedFeedback, is_read: true });
        } catch (err) {
            message.error('Failed to mark as read');
            logger.error('Error marking as read:', err);
        }
    };

    // Handle mark as unread
    const handleMarkAsUnread = async () => {
        if (!selectedFeedback) return;

        try {
            await feedbackService.markAsUnread(selectedFeedback.tenant_id, selectedFeedback.feedback_id);
            message.success('Feedback marked as unread');

            // Update local state
            setFeedback(prevFeedback =>
                prevFeedback.map(fb =>
                    fb.feedback_id === selectedFeedback.feedback_id
                        ? { ...fb, is_read: false }
                        : fb
                )
            );
            setSelectedFeedback({ ...selectedFeedback, is_read: false });
        } catch (err) {
            message.error('Failed to mark as unread');
            logger.error('Error marking as unread:', err);
        }
    };

    // Format date display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    // Helper to get tenant name
    const getTenantName = (tenantId) => {
        const tenant = tenants.find(t => t._id === tenantId || t.id === tenantId);
        return tenant ? tenant.name : tenantId;
    };

    // Define feedback table columns
    const feedbackColumns = [
        {
            title: 'Status',
            dataIndex: 'is_read',
            key: 'is_read',
            width: 100,
            render: (isRead) => (
                <Tag color={isRead ? 'success' : 'warning'}>
                    {isRead ? 'Read' : 'Unread'}
                </Tag>
            )
        },
        {
            title: 'Timestamp',
            dataIndex: 'created_at',  // Backend returns 'created_at'
            key: 'created_at',
            width: 180,
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
            render: (timestamp) => formatDate(timestamp)
        },
        {
            title: 'Tenant',
            dataIndex: 'tenant_id',
            key: 'tenant_id',
            width: 150,
            render: (tenantId) => getTenantName(tenantId)
        },
        {
            title: 'Feature',
            dataIndex: 'feature',  // Backend returns 'feature' (ai_coach, report_writer, archive)
            key: 'feature',
            width: 120,
            render: (feature) => (
                <Tag color="blue">{feature || 'N/A'}</Tag>
            )
        },
        {
            title: 'Rating',
            dataIndex: 'rating',
            key: 'rating',
            width: 100,
            align: 'center',
            sorter: (a, b) => a.rating - b.rating,  // Backend returns integers
            render: (rating) => {
                // Backend returns integers: 1 = positive, -1 = negative
                const isPositive = rating > 0;
                return (
                    <Tag
                        color={isPositive ? 'success' : 'error'}
                        icon={isPositive ? <LikeOutlined /> : <DislikeOutlined />}
                    >
                        {isPositive ? 'Positive' : 'Negative'}
                    </Tag>
                );
            }
        },
        {
            title: 'User Input',
            dataIndex: 'question',  // Backend returns 'question'
            key: 'question',
            width: 250,
            ellipsis: true,
            render: (text) => (
                <Text ellipsis={{ tooltip: text }} style={{ maxWidth: 250 }}>
                    {text || 'N/A'}
                </Text>
            )
        },
        {
            title: 'AI Output',
            dataIndex: 'answer',  // Backend returns 'answer'
            key: 'answer',
            width: 250,
            ellipsis: true,
            render: (text) => (
                <Text ellipsis={{ tooltip: text }} style={{ maxWidth: 250 }}>
                    {text || 'N/A'}
                </Text>
            )
        },
        {
            title: 'Comment',
            dataIndex: 'comment',  // Backend returns 'comment'
            key: 'comment',
            width: 250,
            ellipsis: true,
            render: (text) => (
                <Text ellipsis={{ tooltip: text }} style={{ maxWidth: 250 }}>
                    {text || 'No comment'}
                </Text>
            )
        },
        {
            title: 'User',
            dataIndex: 'user_name',  // Backend returns 'user_name'
            key: 'user_name',
            width: 150,
            render: (userName) => (
                <Text type="secondary" style={{ fontSize: '12px' }}>{userName || 'N/A'}</Text>
            )
        }
    ];

    return (
        <div style={{ height: 'calc(100vh - 56px)', overflow: 'auto', background: 'var(--rlc-surface-sunken)' }}>
            <div style={{ padding: '24px' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Header */}
                <Card>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <div>
                            <h2 style={{ margin: 0 }}>User Feedback</h2>
                            <Text type="secondary">Click a row to view details and mark as read/unread</Text>
                        </div>
                        <Space>
                            <Select
                                value={statusFilter}
                                onChange={(value) => setStatusFilter(value)}
                                style={{ width: 150 }}
                            >
                                <Option value="all">All Feedback</Option>
                                <Option value="unread">Unread Only</Option>
                                <Option value="read">Read Only</Option>
                            </Select>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadFeedback}
                                loading={loading}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </Space>
                </Card>

                {/* Feedback Table */}
                <Card>
                    <Table
                        columns={feedbackColumns}
                        dataSource={feedback}
                        loading={loading}
                        rowKey={(record) => record.feedback_id}
                        onRow={(record) => ({
                            onClick: () => handleRowClick(record),
                            style: {
                                cursor: 'pointer',
                                background: record.is_read ? 'transparent' : 'var(--rlc-state-warning-bg)'
                            }
                        })}
                        pagination={{
                            pageSize: 25,
                            showSizeChanger: true,
                            showTotal: (total) => `Total ${total} feedback items`
                        }}
                        scroll={{ x: 1500 }}
                    />
                </Card>
            </Space>
            </div>

            {/* Feedback Detail Drawer */}
            <Drawer
                title="Feedback Details"
                placement="right"
                width="70%"
                onClose={() => {
                    setDrawerOpen(false);
                    setSelectedFeedback(null);
                }}
                open={drawerOpen}
                extra={
                    <Space>
                        {selectedFeedback?.is_read ? (
                            <Button
                                icon={<CloseCircleOutlined />}
                                onClick={handleMarkAsUnread}
                            >
                                Mark as Unread
                            </Button>
                        ) : (
                            <Button
                                icon={<CheckCircleOutlined />}
                                type="primary"
                                onClick={handleMarkAsRead}
                            >
                                Mark as Read
                            </Button>
                        )}
                    </Space>
                }
                styles={{
                    body: { padding: '24px', overflowY: 'auto' }
                }}
            >
                {selectedFeedback && (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* Status Badge */}
                        <Card size="small" style={{ background: selectedFeedback.is_read ? 'var(--rlc-state-success-bg)' : 'var(--rlc-state-warning-bg)' }}>
                            <div style={{ textAlign: 'center' }}>
                                <Tag
                                    icon={selectedFeedback.is_read ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                    color={selectedFeedback.is_read ? 'success' : 'warning'}
                                    style={{ fontSize: 14, padding: '4px 12px' }}
                                >
                                    {selectedFeedback.is_read ? 'Read' : 'Unread'}
                                </Tag>
                            </div>
                        </Card>

                        {/* Main Details */}
                        <Descriptions title="Overview" column={1} bordered size="small">
                            <Descriptions.Item label="Timestamp">
                                {formatDate(selectedFeedback.created_at)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Tenant">
                                {getTenantName(selectedFeedback.tenant_id)}
                            </Descriptions.Item>
                            <Descriptions.Item label="User">
                                <div>
                                    <div>{selectedFeedback.user_name || 'N/A'}</div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {selectedFeedback.user_email || 'N/A'}
                                    </Text>
                                </div>
                            </Descriptions.Item>
                            <Descriptions.Item label="Feature">
                                <Tag color="blue">{selectedFeedback.feature || 'N/A'}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Rating">
                                {selectedFeedback.rating > 0 ? (
                                    <Tag color="success" icon={<LikeOutlined />}>Positive</Tag>
                                ) : (
                                    <Tag color="error" icon={<DislikeOutlined />}>Negative</Tag>
                                )}
                            </Descriptions.Item>
                        </Descriptions>

                        {/* Content */}
                        <Descriptions title="Content" column={1} bordered size="small">
                            <Descriptions.Item label="User Input">
                                <div style={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedFeedback.question || 'N/A'}
                                </div>
                            </Descriptions.Item>
                            <Descriptions.Item label="AI Output">
                                <div style={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedFeedback.answer || 'N/A'}
                                </div>
                            </Descriptions.Item>
                            <Descriptions.Item label="User Comment">
                                <div style={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedFeedback.comment || 'No comment provided'}
                                </div>
                            </Descriptions.Item>
                        </Descriptions>

                        {/* Review Info */}
                        {selectedFeedback.is_read && (
                            <Descriptions title="Review Information" column={1} bordered size="small">
                                <Descriptions.Item label="Reviewed By">
                                    {selectedFeedback.reviewed_by || 'N/A'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Reviewed At">
                                    {selectedFeedback.reviewed_at ? formatDate(selectedFeedback.reviewed_at) : 'N/A'}
                                </Descriptions.Item>
                            </Descriptions>
                        )}
                    </Space>
                )}
            </Drawer>
        </div>
    );
};

export default Feedback;
